/**
 * Given a uri string with possible embedded variables, this function builds the
 * final uri string replacing in any variables needed if provided.
 * 
 * Looking at a quick example, using the variables `{ env: 'qa' }`, the uri
 * `(env).site.com` becomes `qa.site.com`.
 * 
 * Non-alphanumeric characters inside of the parenthesis in the uri string will
 * remain after the variable replace, unless the replace value is an empty string.
 * Looking at the example above again, this allows us to use `(env.)site.com` as
 * the uri, and if we use the replace variables `{ env: 'qa' }`, the uri becomes
 * `qa.site.com`. Similarily, if we use the variables `{ env: '' }`, we'd get the
 * uri `site.com`.
 * 
 * @param { string | string[] } uri - The URI with possible replaceable substrings.
 * @param { object } replaceVariables - Any data to potentially use as replacement values in the uri string.
 * @param { object } queryStringData - Any data to include as query string parameters at the end of the built URI.
 * @returns { string }
 */
export function buildUri( uri, replaceVariables, queryStringData ) {
    // If the provided URI is an array of strings, attempt to combine of of them into one.
    if ( typeof uri === 'object' && Array.isArray( uri ) ) {
        let updatedUri = '';
        for ( let i = 0; i < uri.length; i++ ) {
            let token = uri[ i ];

            // Remove the '/' at the start and end of the token if there they are there.
            if ( token?.charAt( 0 ) === '/' ) {
                token = token.slice( 1 );
            }
            if ( token?.charAt( token.length - 1 ) === '/' ) {
                token = token.slice( 0, token.length - 1 );
            }

            if ( token ) {
                updatedUri = ( !updatedUri ) ? token : `${ updatedUri }/${ token }`;
            }
        }

        // Replace URI value with new one (not array)
        uri = updatedUri;
    }

    // Find and replace variables wrapped in parenthesis.
    const regExp = /\(([^)]+)\)/g;
    const result = uri?.replace( regExp, ( match ) => {
        const varStr = match.substring( 1, match.length - 1 );
        const varKey = varStr.replace( /\W/g, '' );
        const replaceValue = replaceVariables ? replaceVariables[ varKey ] : undefined;
        const neighboringCharacters = varStr.split( varKey );

        // If a replace value has been found, replace the variable with the found value.
        if ( replaceValue !== undefined ) {
            return ( replaceValue )
                ? neighboringCharacters[ 0 ] + replaceValue + neighboringCharacters[ 1 ]
                : replaceValue;
        }

        return match;
    } );

    // Add the query string param string to the URI if needed.
    let keys = Object.keys( queryStringData || {} );
    let queryString = '';
    for ( let i = 0; i < keys.length; i++ ) {
        const leadingChar = ( i == 0 ) ? '?' : '&';
        const key = keys[ i ];
        const value = queryStringData[ key ];
        if ( typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ) {
            queryString += `${ leadingChar }${ key }=${ value }`;
        }
    }

    return result + queryString;
}
